<template>
    <div class="bd">
        <div class="my-custom-table">
            <a-page-header title="商品管理" />
            <div class="search-bar">
                <div class="tabInput">
                    <a class="query-label">商品名称：</a>
                    <a-input v-model:value="query.productName" placeholder="请输入商品名称" class="search-input" allow-clear
                        @press-enter="onSearch" />
                </div>
                <div class="tabInput">
                    <a class="query-label">服务标识：</a>
                    <a-select v-model:value="query.serviceCode" placeholder="请选择" class="search-select" allow-clear
                        :options="SERVICE_OPTIONS" />
                </div>
                <div class="tabInput">
                    <a class="query-label">计费模式：</a>
                    <a-select v-model:value="query.chargeMode" placeholder="请选择" class="search-select" allow-clear
                        :options="CHARGE_MODE_OPTIONS" />
                </div>
                <div class="tabInput">
                    <a class="query-label">状态：</a>
                    <a-select v-model:value="query.status" placeholder="请选择" class="search-select" allow-clear
                        :options="STATUS_OPTIONS" />
                </div>
                <div class="tab-button">
                    <div>
                        <a-button class="search-tow" type="primary" @click="onSearch"
                            :icon="h(SearchOutlined)">搜索</a-button>
                        <a-button class="search-tow" @click="onReset" :icon="h(RedoOutlined)">重置</a-button>
                    </div>
                    <div class="right-option">
                        <a-button v-if="canManage" type="primary" @click="toAdd"
                            :icon="h(PlusCircleOutlined)">新增商品</a-button>
                    </div>
                </div>
            </div>

            <a-table :columns="columns" :data-source="data" :pagination="pagination" row-key="id"
                :scroll="{ y: 'calc(100vh - 360px)' }" class="flex-table">
                <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'serviceCode'">
                        <a-tag color="blue">{{ serviceLabel(record.serviceCode) }}</a-tag>
                    </template>
                    <template v-if="column.key === 'chargeMode'">
                        {{ chargeModeLabel(record.chargeMode) }}
                    </template>
                    <template v-if="column.key === 'price'">
                        <span class="now-price">￥{{ formatYuan(record.price) }}</span>
                        <span v-if="record.originalPrice > 0" class="origin-price">￥{{
                            formatYuan(record.originalPrice) }}</span>
                    </template>
                    <template v-if="column.key === 'times'">
                        {{ timesLabel(record) }}
                    </template>
                    <template v-if="column.key === 'giftTimes'">
                        <span v-if="record.giftServiceCode && record.giftTimes">赠{{ record.giftTimes }}次（{{
                            serviceLabel(record.giftServiceCode) }}）</span>
                        <span v-else>-</span>
                    </template>
                    <template v-if="column.key === 'status'">
                        <a-switch :checked="record.status === 'ON'" :loading="switchingId === record.id"
                            :disabled="!canManage" checked-children="上架" un-checked-children="下架"
                            @change="(checked) => toggleStatus(record, checked)" />
                    </template>
                    <template v-if="column.dataIndex === 'operation'">
                        <div v-if="canManage" class="editable-row-operations">
                            <a @click="edit(record)">编辑</a>
                            <a-divider type="vertical" />
                            <a-popconfirm title="确认删除该商品？" ok-text="是" cancel-text="否"
                                @confirm="remove(record.id)">
                                <a style="margin: 15px;">删除</a>
                            </a-popconfirm>
                        </div>
                        <span v-else>-</span>
                    </template>
                </template>
            </a-table>

            <addComponent ref="childOpen" @saved="onSaved" />
        </div>
    </div>
</template>

<script>
import { commodityPage, updateCommodity, removeCommodity } from '@/api/service';
import { message } from 'ant-design-vue';
import { SearchOutlined, RedoOutlined, PlusCircleOutlined } from '@ant-design/icons-vue';
import { ref, reactive, h, computed } from 'vue';
import { useUserStore } from '@/store/index';
import addComponent from './modal/addComponent.vue';

// 服务标识（后端支持扩展，管理端固定这两个内置值）
const SERVICE_OPTIONS = [
    { value: 'NORMAL_CONSULT', label: '普通咨询' },
    { value: 'MEMBER', label: '专属会员' },
];
const CHARGE_MODE_OPTIONS = [
    { value: 'COUNT', label: '按次' },
    { value: 'DURATION', label: '按天' },
];
const STATUS_OPTIONS = [
    { value: 'ON', label: '上架' },
    { value: 'OFF', label: '下架' },
];

export default {
    name: 'CommodityPage',
    components: {
        addComponent
    },
    setup() {
        const data = ref([]);
        const childOpen = ref(null);
        const switchingId = ref('');
        const userStore = useUserStore();
        const query = reactive({
            productName: '',
            serviceCode: undefined,
            chargeMode: undefined,
            status: undefined,
        });

        // 新增/编辑/删除按钮权限：ADMIN / SUPER_ADMIN 可见
        // 权限数据缺失时不隐藏按钮，由后端 @SaCheckRole 拦截
        const canManage = computed(() => {
            const perms = userStore.permissions;
            const list = Array.isArray(perms) ? perms : (perms ? [perms] : []);
            if (list.length === 0) {
                return true;
            }
            return list.some(item => {
                const code = typeof item === 'string' ? item : (item && item.permissionCode);
                return code === 'ADMIN' || code === 'SUPER_ADMIN';
            });
        });

        const columns = [
            { title: '商品编码', dataIndex: 'productCode', key: 'productCode', width: '13%' },
            { title: '商品名称', dataIndex: 'productName', key: 'productName', width: '15%' },
            { title: '服务标识', dataIndex: 'serviceCode', key: 'serviceCode', width: '11%' },
            { title: '计费模式', dataIndex: 'chargeMode', key: 'chargeMode', width: '9%' },
            { title: '价格', dataIndex: 'price', key: 'price', width: '14%' },
            { title: '次数/天数', dataIndex: 'times', key: 'times', width: '9%' },
            { title: '赠送', dataIndex: 'giftTimes', key: 'giftTimes', width: '11%' },
            { title: '状态', dataIndex: 'status', key: 'status', width: '8%' },
            { title: '排序', dataIndex: 'sortNo', key: 'sortNo', width: '6%' },
            { title: '操作', dataIndex: 'operation', key: 'operation', width: '10%' },
        ];

        const pagination = ref({
            current: 1,
            pageSize: 10,
            total: 0,
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: (page, pageSize) => {
                pagination.value.current = page;
                pagination.value.pageSize = pageSize;
                list();
            },
            position: ['bottomLeft']
        });

        // 金额：分 → 元（1050 → 10.50）
        const formatYuan = (fen) => (Number(fen || 0) / 100).toFixed(2);
        const serviceLabel = (code) => {
            const hit = SERVICE_OPTIONS.find(item => item.value === code);
            return hit ? hit.label : (code || '-');
        };
        const chargeModeLabel = (mode) => {
            const hit = CHARGE_MODE_OPTIONS.find(item => item.value === mode);
            return hit ? hit.label : (mode || '-');
        };
        const timesLabel = (record) => {
            if (record.chargeMode === 'DURATION') {
                return record.validDays ? `${record.validDays}天` : '-';
            }
            return record.times ? `${record.times}次` : '-';
        };

        const list = async () => {
            const response = await commodityPage({
                pageNumber: pagination.value.current,
                pageSize: pagination.value.pageSize,
                productName: query.productName || undefined,
                serviceCode: query.serviceCode || undefined,
                chargeMode: query.chargeMode || undefined,
                status: query.status || undefined,
            });
            if (response.code === 200) {
                const page = response.data || {};
                pagination.value.total = page.totalRow || page.total || response.totalRow || 0;
                data.value = page.records || [];
            } else {
                message.error(response.msg || '查询失败');
            }
        };

        list();

        // 上下架：后端 update 是全量校验，不能只传 {id,status}，须带齐业务字段整对象提交
        const buildSubmitPayload = (record) => {
            const payload = {
                id: record.id,
                productCode: record.productCode,
                productName: record.productName,
                serviceCode: record.serviceCode,
                description: record.description,
                chargeMode: record.chargeMode,
                price: record.price,
                status: record.status,
                sortNo: record.sortNo ?? 0,
            };
            // 原价<=0 在后端等价于"未配置"，统一传 0，避免 null 被 updateById 忽略后旧值残留
            payload.originalPrice = record.originalPrice > 0 ? record.originalPrice : 0;
            if (record.chargeMode === 'COUNT') {
                payload.times = record.times;
                if (record.validDays != null) {
                    payload.validDays = record.validDays;
                }
            } else {
                payload.validDays = record.validDays;
                payload.times = 0;
            }
            if (record.giftServiceCode && record.giftTimes) {
                payload.giftServiceCode = record.giftServiceCode;
                payload.giftTimes = record.giftTimes;
            }
            return payload;
        };

        const toggleStatus = async (record, checked) => {
            const oldStatus = record.status;
            const newStatus = checked ? 'ON' : 'OFF';
            record.status = newStatus;
            switchingId.value = record.id;
            const response = await updateCommodity(buildSubmitPayload(record));
            switchingId.value = '';
            if (response.code === 200) {
                message.success(newStatus === 'ON' ? '已上架' : '已下架');
                list();
            } else {
                // 失败回滚开关显示
                record.status = oldStatus;
                message.error(response.msg || '操作失败');
            }
        };

        const remove = async (id) => {
            const response = await removeCommodity(id);
            if (response.code === 200) {
                message.success('删除成功');
                list();
            } else {
                message.error(response.msg || '删除失败');
            }
        };

        const edit = (record) => {
            childOpen.value.openEdit(record);
        };

        const toAdd = () => {
            childOpen.value.openAdd();
        };

        const onSearch = () => {
            pagination.value.current = 1;
            list();
        };

        const onReset = () => {
            query.productName = '';
            query.serviceCode = undefined;
            query.chargeMode = undefined;
            query.status = undefined;
            pagination.value.current = 1;
            list();
        };

        const onSaved = () => {
            list();
        };

        return {
            SERVICE_OPTIONS,
            CHARGE_MODE_OPTIONS,
            STATUS_OPTIONS,
            columns,
            pagination,
            data,
            query,
            childOpen,
            switchingId,
            canManage,
            SearchOutlined,
            RedoOutlined,
            PlusCircleOutlined,
            formatYuan,
            serviceLabel,
            chargeModeLabel,
            timesLabel,
            toggleStatus,
            remove,
            edit,
            toAdd,
            onSearch,
            onReset,
            onSaved,
            h,
        };
    },
};
</script>

<style scoped>
.search-bar {
    flex-wrap: wrap;
    align-items: center;
}

.tabInput {
    width: auto;
    margin: 0 12px 10px 0;
}

.query-label {
    white-space: nowrap;
}

.search-select {
    width: 150px;
}

.tab-button {
    width: auto;
    flex: 1;
    margin-bottom: 10px;
}

.now-price {
    color: #e74c3c;
    font-weight: bold;
}

.origin-price {
    margin-left: 8px;
    color: #999;
    text-decoration: line-through;
}
</style>
